import React, { useState, useRef, useEffect, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import { getMfaMethods, verifyStepUp } from "../../crud/auth.crud";

const StepUpMfaModal = ({ show, onHide, onSuccess, bucket = "security" }) => {
  const [methods, setMethods] = useState([]);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [code, setCode] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMethods, setLoadingMethods] = useState(false);
  const [error, setError] = useState("");
  const inputRefs = useRef([]);
  const singleInputRef = useRef(null);

  const loadMethods = useCallback(async () => {
    setLoadingMethods(true);
    setError("");
    try {
      const res = await getMfaMethods();
      const { methods: availableMethods, defaultMethod } = res.data;
      setMethods(availableMethods);
      const defaultMethodObj =
        availableMethods.find((m) => m.type === defaultMethod) ||
        availableMethods[0];
      setSelectedMethod(defaultMethodObj);
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Erro ao carregar métodos de verificação."
      );
      setMethods([]);
      setSelectedMethod(null);
    } finally {
      setLoadingMethods(false);
    }
  }, []);

  useEffect(() => {
    if (show) {
      loadMethods();
    }
  }, [show, loadMethods]);

  useEffect(() => {
    if (!selectedMethod) return;

    const { inputConfig } = selectedMethod;
    setCode(Array(inputConfig.length).fill(""));

    setTimeout(() => {
      if (inputConfig.type === "alphanumeric") {
        if (singleInputRef.current) singleInputRef.current.focus();
      } else if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 100);
  }, [selectedMethod]);

  const handleClose = () => {
    setCode([]);
    setMethods([]);
    setSelectedMethod(null);
    setError("");
    onHide();
  };

  const resetState = () => {
    setCode([]);
    setMethods([]);
    setSelectedMethod(null);
    setError("");
  };

  const handleVerify = async () => {
    const fullCode = code.join("");
    setLoading(true);
    setError("");

    try {
      await verifyStepUp(fullCode, bucket);
      resetState();
      onSuccess();
    } catch (err) {
      const message =
        err.response?.data?.message || "Código inválido. Tente novamente.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleNumericChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);

    if (value && index < code.length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (
      e.key === "Backspace" &&
      !code[index] &&
      index > 0 &&
      inputRefs.current[index - 1]
    ) {
      inputRefs.current[index - 1].focus();
    }

    if (e.key === "Enter" && isCodeComplete()) {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    if (!selectedMethod) return;
    const { inputConfig } = selectedMethod;
    const pastedData = e.clipboardData
      .getData("text")
      .replace(inputConfig.type === "numeric" ? /\D/g : /[^a-zA-Z0-9]/g, "")
      .slice(0, inputConfig.length);

    if (inputConfig.type === "alphanumeric") {
      setCode(pastedData.toUpperCase().split(""));
    } else {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length; i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);
    }
  };

  const handleAlphanumericChange = (e) => {
    if (!selectedMethod) return;
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    const { inputConfig } = selectedMethod;
    setCode(value.slice(0, inputConfig.length).split(""));
  };

  const switchMethod = (method) => {
    if (method.type === selectedMethod?.type) return;
    setSelectedMethod(method);
    setError("");
  };

  const isCodeComplete = () => {
    return (
      selectedMethod &&
      code.join("").length === selectedMethod.inputConfig.length
    );
  };

  const renderNumericInput = () => {
    if (!selectedMethod) return null;
    const { inputConfig } = selectedMethod;

    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: "8px",
          marginBottom: "20px",
        }}
      >
        {Array.from({ length: inputConfig.length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={code[index] || ""}
            onChange={(e) => handleNumericChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            style={{
              width: "40px",
              height: "50px",
              fontSize: "20px",
              textAlign: "center",
              border: "1px solid #e2e5ec",
              borderRadius: "4px",
            }}
            disabled={loading}
          />
        ))}
      </div>
    );
  };

  const renderAlphanumericInput = () => {
    if (!selectedMethod) return null;
    const { inputConfig } = selectedMethod;

    return (
      <input
        ref={singleInputRef}
        type="text"
        className="form-control mb-3"
        placeholder={inputConfig.placeholder || ""}
        value={code.join("")}
        onChange={handleAlphanumericChange}
        onPaste={handlePaste}
        onKeyDown={(e) => {
          if (e.key === "Enter" && isCodeComplete()) handleVerify();
        }}
        maxLength={inputConfig.length}
        style={{
          textAlign: "center",
          fontSize: "18px",
          letterSpacing: "2px",
          textTransform: "uppercase",
        }}
        disabled={loading}
      />
    );
  };

  const renderInput = () => {
    if (!selectedMethod) return null;
    const { inputConfig } = selectedMethod;
    return inputConfig.type === "numeric"
      ? renderNumericInput()
      : renderAlphanumericInput();
  };

  const renderMethodSwitch = () => {
    if (methods.length <= 1) return null;

    const otherMethods = methods.filter((m) => m.type !== selectedMethod?.type);

    return (
      <div className="text-center mb-3">
        {otherMethods.map((method) => (
          <button
            key={method.type}
            type="button"
            onClick={() => switchMethod(method)}
            disabled={loading}
            style={{
              fontSize: "13px",
              color: "#5867dd",
              textDecoration: "underline",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 0,
            }}
          >
            {method.type === "backup_code"
              ? "Usar código de backup"
              : "Usar código do aplicativo"}
          </button>
        ))}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>Verificação de Segurança</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingMethods ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Carregando...</span>
            </div>
          </div>
        ) : methods.length === 0 ? (
          <div className="text-center">
            <div className="alert alert-warning">
              <strong>MFA não habilitado</strong>
              <p className="mb-0 mt-2" style={{ fontSize: "14px" }}>
                Para realizar esta ação, você precisa habilitar a autenticação
                de dois fatores nas configurações de segurança.
              </p>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <p className="mb-3">
              Esta ação requer verificação adicional para proteger sua conta.
            </p>
            <p className="text-muted mb-3">
              {selectedMethod?.hint || "Digite o código de verificação:"}
            </p>
            {renderInput()}
            {renderMethodSwitch()}
            {error && <div className="alert alert-danger">{error}</div>}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          {methods.length === 0 ? "Fechar" : "Cancelar"}
        </Button>
        {methods.length > 0 && (
          <Button
            variant="primary"
            onClick={handleVerify}
            disabled={loading || loadingMethods || !isCodeComplete()}
          >
            {loading ? "Verificando..." : "Verificar"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default StepUpMfaModal;
